import React, { useMemo, useState } from 'react';
import { Check, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import EmailQuoteDialog from './EmailQuoteDialog';

/**
 * Podcast production pricing estimator.
 *
 * Structured to mirror how clients actually scale up with us:
 *
 *   1. BASE — "Full episode, all-in" £120 (edit, show notes, chapters,
 *      transcript, upload) or "Editing only" £90 with single-priced extras.
 *
 *   2. Add-ons stack up cumulatively so a visitor can see, live, what it
 *      costs to move from "just editing" all the way to the Pro Plan's
 *      12-outputs-a-week engine.
 *
 * A la carte pricing is intentionally above the £280/week Pro Plan bundle
 * so the bundle is the obvious value option. If a visitor's selections
 * cross the Pro Plan threshold we surface a nudge to switch.
 *
 * Prices signed off by Jo (Feb 2026). All numbers are per episode and
 * multiply by the frequency choice below.
 */

const BASES = [
  { id: 'full', label: 'Full episode, all-in', desc: 'Edit, mix + master, show notes + timestamps, chapter markers, full transcript, uploaded and scheduled on your hosting platform.', price: 120 },
  { id: 'edit', label: 'Editing only', desc: 'Edit, mix + master (noise cleanup, EQ, level matching, -16 LUFS). You publish it yourself.', price: 90 },
];
const PRO_PLAN_WEEKLY = 280;
const PRO_PLAN_MONTHLY = 1213;

const FREQUENCIES = [
  { id: 'weekly',      label: 'Weekly',      hint: '4 episodes / month',  epsPerMonth: 4.33 },
  { id: 'fortnightly', label: 'Fortnightly', hint: '2 episodes / month',  epsPerMonth: 2.17 },
  { id: 'monthly',     label: 'Monthly',     hint: '1 episode / month',   epsPerMonth: 1    },
];

const ADD_ON_GROUPS = [
  {
    id: 'basics',
    label: 'Individual extras (priced singly with editing only)',
    editOnly: true,
    items: [
      { id: 'show-notes',   label: 'Show notes + timestamps',            desc: 'SEO-friendly episode description + chapter timestamps', price: 20 },
      { id: 'chapters',     label: 'Chapter markers',                    desc: 'Embedded in the audio for Apple / Overcast',            price: 8  },
      { id: 'publishing',   label: 'Publish to Apple, Spotify, hosting', desc: 'Uploaded, metadata, distribution submitted',            price: 30 },
      { id: 'transcript',   label: 'Full transcript (SRT + TXT)',        desc: 'Searchable transcript, ready for accessibility use',    price: 20 },
    ],
  },
  {
    id: 'extras',
    label: 'Episode extras',
    items: [
      { id: 'cover-art',    label: 'Episode cover art',                  desc: 'Per-episode artwork matched to your show',              price: 20 },
    ],
  },
  {
    id: 'video',
    label: 'YouTube + video',
    items: [
      { id: 'youtube-long', label: 'Long-form YouTube video',            desc: 'Full episode as branded YouTube video + thumbnail',     price: 45 },
      { id: 'shorts-3',     label: '3 vertical Shorts / Reels',          desc: 'Captioned vertical clips for Shorts, Reels, TikTok',    price: 40 },
    ],
  },
  {
    id: 'written',
    label: 'Written content',
    items: [
      { id: 'blog-post',    label: 'SEO blog post from the episode',     desc: 'Long-form, keyword-researched, links back to episode',  price: 70 },
      { id: 'email',        label: 'Weekly email newsletter',            desc: 'One email to your list, written from the episode',      price: 40 },
    ],
  },
  {
    id: 'social',
    label: 'Social + website',
    items: [
      { id: 'community',    label: '7 YouTube Community posts',          desc: 'One per day, drawn from the episode',                   price: 35 },
      { id: 'social-5',     label: '5 social posts',                     desc: 'LinkedIn, X, Instagram or Facebook',                    price: 30 },
      { id: 'website',      label: 'Business website update',            desc: 'Episode added to your Authority Hub, interlinked',      price: 20 },
    ],
  },
];

// All Pro Plan-equivalent add-ons in one array for the "match Pro Plan" preset.
const PRO_PLAN_ADD_ONS = [
  'youtube-long', 'shorts-3',
  'blog-post', 'email',
  'community', 'social-5', 'website',
];

function gbp(n) {
  return `£${Math.round(n).toLocaleString()}`;
}

export default function PodcastEstimator({ testId = 'podcast-estimator' }) {
  const [frequencyId, setFrequencyId] = useState('weekly');
  const [baseId, setBaseId] = useState('full');
  const base = BASES.find((b) => b.id === baseId);
  const groups = ADD_ON_GROUPS.filter((g) => !g.editOnly || baseId === 'edit');
  const [selected, setSelected] = useState(new Set()); // add-on ids
  const [emailOpen, setEmailOpen] = useState(false);

  const frequency = FREQUENCIES.find((f) => f.id === frequencyId);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const matchProPlan = () => { setBaseId('full'); setSelected(new Set(PRO_PLAN_ADD_ONS)); };
  const clearAll = () => { setBaseId('edit'); setSelected(new Set()); };

  const { perEpisode, monthly, addOnBreakdown, overProPlan } = useMemo(() => {
    let addOnPerEp = 0;
    const rows = [];
    for (const group of groups) {
      for (const item of group.items) {
        if (selected.has(item.id)) {
          addOnPerEp += item.price;
          rows.push({ id: item.id, label: item.label, price: item.price });
        }
      }
    }
    const perEp = base.price + addOnPerEp;
    const perMonth = perEp * frequency.epsPerMonth;
    return {
      perEpisode: perEp,
      monthly: perMonth,
      addOnBreakdown: rows,
      overProPlan: perMonth > PRO_PLAN_MONTHLY,
    };
  }, [selected, frequency, base, groups]);

  return (
    <div
      className="bg-white rounded-[16px] border border-[#E2E8F0] p-6 sm:p-8 shadow-[0_18px_40px_-24px_rgba(30,42,68,0.35)]"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h3 className="font-display font-semibold text-[#1E2A44] text-xl sm:text-2xl">
          Estimate your podcast
        </h3>
        <p className="text-xs uppercase tracking-widest text-[#94A3B8]">Indicative ballpark</p>
      </div>
      <p className="text-[#475569] mt-2 text-sm leading-relaxed">
        A full episode is £120, all-in: edited, mixed and mastered, with show notes, chapters, a full transcript,
        and uploaded to your hosting platform. Only need the edit? Editing only is £90. Video, written and social
        extras are optional add-ons.
      </p>

      {/* Quick actions */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid={`${testId}-quick`}>
        <button
          type="button"
          onClick={matchProPlan}
          className="rounded-[10px] border border-[#BC516F] bg-white text-[#BC516F] hover:bg-[#FFF3F6] font-semibold text-xs uppercase tracking-widest px-3.5 py-2 transition-colors"
          data-testid={`${testId}-match-pro-plan`}
        >
          Match the Pro Plan
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="rounded-[10px] border border-[#E2E8F0] bg-white text-[#475569] hover:border-[#94A3B8] font-medium text-xs uppercase tracking-widest px-3.5 py-2 transition-colors"
          data-testid={`${testId}-clear`}
        >
          Audio only
        </button>
      </div>

      {/* Frequency */}
      <div className="mt-6">
        <p className="text-[#1E2A44] font-semibold text-sm mb-2">How often do you publish?</p>
        <div className="flex flex-wrap gap-2">
          {FREQUENCIES.map((f) => {
            const active = frequencyId === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFrequencyId(f.id)}
                aria-pressed={active}
                className={`text-left rounded-[10px] border px-3.5 py-2.5 transition-all ${
                  active
                    ? 'border-[#BC516F] bg-[#BC516F] text-white shadow-[0_6px_16px_-6px_rgba(188,81,111,0.55)]'
                    : 'border-[#E2E8F0] bg-white text-[#1E2A44] hover:border-[#BC516F] hover:bg-[#FFF3F6]'
                }`}
                data-testid={`${testId}-freq-${f.id}`}
              >
                <div className="font-semibold text-sm leading-tight">{f.label}</div>
                <div className={`text-xs mt-0.5 ${active ? 'text-white/85' : 'text-[#64748B]'}`}>{f.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Base choice */}
      <div className="mt-6">
        <p className="text-[#1E2A44] font-semibold text-sm">Choose your episode service</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid={`${testId}-base`}>
          {BASES.map((b) => {
            const on = b.id === baseId;
            return (
              <button key={b.id} type="button" onClick={() => setBaseId(b.id)} className={`text-left flex items-start justify-between gap-3 rounded-[10px] border p-3.5 transition-colors ${on ? 'border-[#BC516F] bg-[#FFF3F6]' : 'border-[#E2E8F0] bg-white hover:border-[#BC516F]/50'}`} data-testid={`${testId}-base-${b.id}`} aria-pressed={on}>
                <div className="flex items-start gap-2.5">
                  <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${on ? 'bg-[#BC516F] text-white' : 'border border-[#CBD5E1]'}`}>{on && <Check className="w-3.5 h-3.5" strokeWidth={3} />}</span>
                  <div>
                    <p className="font-semibold text-[#1E2A44] text-sm leading-tight">{b.label}</p>
                    <p className="text-[#475569] text-xs mt-0.5">{b.desc}</p>
                  </div>
                </div>
                <span className="font-display font-bold text-[#1E2A44] text-sm shrink-0">£{b.price}/ep</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Add-on groups */}
      {groups.map((group) => (
        <div key={group.id} className="mt-6">
          <p className="text-[#1E2A44] font-semibold text-sm mb-2">{group.label}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {group.items.map((item) => {
              const active = selected.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggle(item.id)}
                  aria-pressed={active}
                  className={`text-left rounded-[10px] border p-3 transition-all ${
                    active
                      ? 'border-[#BC516F] bg-[#FFF3F6]'
                      : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
                  }`}
                  data-testid={`${testId}-addon-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-[6px] flex items-center justify-center flex-shrink-0 ${
                          active ? 'bg-[#BC516F] text-white' : 'bg-[#F1F5F9]'
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1E2A44] text-sm leading-tight">{item.label}</p>
                        <p className="text-[#475569] text-xs mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                    <span className="font-display font-bold text-[#1E2A44] text-xs shrink-0">+£{item.price}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Result */}
      <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#94A3B8]">A la carte total</p>
          <p className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl mt-1" data-testid={`${testId}-per-episode`}>
            {gbp(perEpisode)}<span className="text-[#94A3B8] font-normal text-xl"> / episode</span>
          </p>
          <p className="text-[#475569] text-sm mt-1" data-testid={`${testId}-monthly`}>
            ≈ {gbp(monthly)}/month at {frequency.label.toLowerCase()} cadence
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            className="inline-flex items-center justify-center gap-2 border-2 border-[#BC516F] text-[#BC516F] hover:bg-[#FFF3F6] font-semibold px-4 py-3 rounded-[10px] transition-colors text-sm"
            data-testid={`${testId}-email-me`}
          >
            <Mail className="w-4 h-4" /> Email me this quote
          </button>
          <a
            href="https://calendly.com/audioandco/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#BC516F] hover:bg-[#A7468A] text-white font-semibold px-5 py-3 rounded-[10px] transition-colors text-sm"
            data-testid={`${testId}-cta`}
          >
            Get an exact quote
          </a>
        </div>
      </div>

      {/* Pro Plan nudge — surfaces once a la carte crosses the bundle price */}
      {overProPlan && (
        <div className="mt-5 rounded-[12px] border border-[#BC516F] bg-[#1E2A44] p-4 sm:p-5 flex items-start gap-3" data-testid={`${testId}-proplan-nudge`}>
          <ArrowRight className="w-5 h-5 text-[#BC516F] mt-0.5 shrink-0" />
          <div>
            <p className="text-white font-semibold text-sm leading-tight">This adds up to more than the Pro Plan bundle.</p>
            <p className="text-white/75 text-xs mt-1 leading-relaxed">
              A la carte lands at {gbp(monthly)}/month. The Pro Plan gets you the full weekly engine
              (podcast + YouTube + Shorts + blog + email + Community + website) for {gbp(PRO_PLAN_MONTHLY)}/month.
            </p>
            <Link
              to="/pro-plan"
              className="inline-flex items-center gap-1 text-[#BC516F] hover:text-white font-semibold text-xs mt-2"
              data-testid={`${testId}-proplan-link`}
            >
              See the Pro Plan <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}

      <EmailQuoteDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        testId={`${testId}-email`}
        getPayload={() => ({
          service: 'podcast',
          total_label: `${gbp(perEpisode)} / episode · ≈ ${gbp(monthly)}/month at ${frequency.label.toLowerCase()} cadence`,
          line_items: [
            { label: 'Cadence',                detail: frequency.label,                  amount: `${frequency.epsPerMonth === 1 ? '1' : frequency.epsPerMonth.toFixed(2)} eps/month` },
            { label: base.label, detail: base.desc, amount: `£${base.price}/ep` },
            ...addOnBreakdown.map((row) => ({
              label:  row.label,
              detail: 'Add-on',
              amount: `+£${row.price}/ep`,
            })),
          ],
          notes: 'A la carte total per episode = £' + Math.round(perEpisode) + '. Pro Plan bundle = £280/week (£1,213/month) for the full 12-outputs weekly engine.',
        })}
      />
    </div>
  );
}
